/**
 * EditMailshotModal Component
 *
 * Modal for editing mailshot details including name, description, and campaign assignment
 */

import { useState, useEffect } from "react";
import { FaEnvelope, FaTimes, FaSpinner } from "react-icons/fa";
import { db, functions } from "../../firebase";
import { MailshotRepository } from "../../models/MailshotRepository";
import { useCampaigns } from "../../hooks";
import { useToast } from "./Toast";
import "./CreateMailshotModal.scss"; // Reuse the same styles

export const EditMailshotModal = ({
  isOpen,
  mailshot,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();
  const { campaigns, loading: loadingCampaigns } = useCampaigns();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    campaignId: "",
  });
  const [loading, setLoading] = useState(false);

  // Initialize form data when mailshot changes
  useEffect(() => {
    if (mailshot && isOpen) {
      setFormData({
        name: mailshot.name || "",
        description: mailshot.description || "",
        campaignId: mailshot.campaignId || "",
      });
    }
  }, [mailshot, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.warning("Please enter a mailshot name");
      return;
    }

    if (!formData.campaignId) {
      toast.error("Please select a campaign");
      return;
    }

    setLoading(true);
    try {
      const mailshotRepo = new MailshotRepository(db, functions);
      
      // Prepare updates
      const updates = {
        name: formData.name.trim(),
        description: formData.description.trim(),
      };

      // If campaign changed, update it
      if (formData.campaignId !== mailshot.campaignId) {
        updates.campaignId = formData.campaignId;
      }

      await mailshotRepo.update(mailshot.id, updates);

      toast.success("Mailshot updated successfully");

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error updating mailshot:", error);
      if (error.code === "permission-denied") {
        toast.error("Permission denied: Only admins can edit mailshots.");
      } else {
        toast.error("Failed to update mailshot: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen || !mailshot) return null;

  return (
    <div className="create-mailshot-modal-overlay" onClick={handleClose}>
      <div
        className="create-mailshot-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="create-mailshot-modal__header">
            <div className="create-mailshot-modal__title">
              <FaEnvelope />
              Edit Mailshot
            </div>
            <button
              type="button"
              className="create-mailshot-modal__close"
              onClick={handleClose}
              disabled={loading}
            >
              <FaTimes />
            </button>
          </div>

          <div className="create-mailshot-modal__body">
            <div className="form-group">
              <label htmlFor="campaign">
                Campaign <span className="required">*</span>
              </label>
              <select
                id="campaign"
                name="campaignId"
                value={formData.campaignId}
                onChange={handleChange}
                required
                disabled={loading || loadingCampaigns}
              >
                <option value="">Select a campaign...</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
              {campaigns.length === 0 && !loadingCampaigns && (
                <span className="help-text" style={{ color: "#ef4444" }}>
                  No campaigns available. Please create a campaign first.
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="name">
                Mailshot Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Wave 1 - New Listings"
                required
                disabled={loading}
                autoFocus
              />
              <span className="help-text">
                Give this mailshot a descriptive name to identify it within the
                campaign
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="description">
                Description <span style={{ color: "#6b7280" }}>(optional)</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Add notes or details about this mailshot..."
                disabled={loading}
                rows={3}
              />
            </div>
          </div>

          <div className="create-mailshot-modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading || !formData.name.trim() || !formData.campaignId}
            >
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Updating...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
